"use client";

import { useState, useEffect, useTransition } from "react";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import StandaloneOTPInput from "@/components/AuthComponents/AuthUI/otp-input";
import { useActionState } from "react";
import { verifyPhoneOtpAction, sendPhoneOtpAction } from "@/app/actions/phone-verification";
import { appendSecurityLogFormData } from "@/lib/security-log-client";
import { useTranslations } from "next-intl";

interface VerifyPhoneProps {
  phoneNumber: string;
  verificationId: string; // Reçue depuis PhoneNumber
  onSuccessAction: () => void;
  onErrorAction: (error: string) => void;
  onNextAction: () => void;
  onBackAction: () => void;
}

type AuthActionResponse = {
  error?: string;
  success?: boolean | string;
  data?: any;
};

export default function VerifyPhone({
  phoneNumber,
  verificationId,
  onNextAction,
  onBackAction,
  onErrorAction,
  onSuccessAction,
}: VerifyPhoneProps) {
  const t = useTranslations("auth");
  const tCommon = useTranslations("common");
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isPending, startTransition] = useTransition();

  // Action : Vérifier l'OTP
  const [verifyPhoneState, runVerifyPhone] = useActionState<AuthActionResponse, FormData>(
    async (prevState, formData) => verifyPhoneOtpAction(formData),
    {}
  );

  // Action : Renvoi de l'OTP
  const [sendPhoneState, runSendPhone] = useActionState<AuthActionResponse, FormData>(
    async (prevState, formData) => sendPhoneOtpAction(formData),
    {}
  );

  useEffect(() => {
    if (verifyPhoneState.error) {
      setErrorMessage(verifyPhoneState.error);
      onErrorAction(verifyPhoneState.error);
    } else if (verifyPhoneState.success) {
      onNextAction();
      onSuccessAction();
    }
    setLoading(false);
  }, [verifyPhoneState, onNextAction, onErrorAction, onSuccessAction]);

  useEffect(() => {
    if (!sendPhoneState.success && !sendPhoneState.error) return;
    if (isResending) {
      if (sendPhoneState.error) {
        setErrorMessage(sendPhoneState.error || t("resendErrorPhone"));
        onErrorAction(sendPhoneState.error || t("resendErrorPhone"));
      } else if (sendPhoneState.success) {
        setInfoMessage(t("newCodeSentToPhone"));
      }
      setIsResending(false);
    }
    setLoading(false);
  }, [sendPhoneState, isResending, onErrorAction]);

  // Vérification automatique lorsque 6 chiffres sont saisis
  const handleAutoVerify = (code: string) => {
    if (code.length !== 6) {
      setErrorMessage(t("enterSixDigitVerification"));
      return;
    }
    if (loading) return;
    setLoading(true);
    setErrorMessage("");
    setInfoMessage("");
    const formData = new FormData();
    formData.append("verificationId", verificationId);
    formData.append("phoneNumber", phoneNumber);
    formData.append("otp", code);
    appendSecurityLogFormData(formData);
    startTransition(() => runVerifyPhone(formData));
  };

  // Renvoi de l'OTP
  const handleResend = () => {
    setLoading(true);
    setErrorMessage("");
    setInfoMessage("");
    setIsResending(true);
    const formData = new FormData();
    formData.append("phoneNumber", phoneNumber);
    startTransition(() => runSendPhone(formData));
  };

  return (
    <div className="text-center space-y-6 pt-2 relative">
      <button
        onClick={onBackAction}
        className="absolute -top-1 -left-1 min-w-[44px] min-h-[44px] flex items-center justify-center p-2.5 hover:bg-muted/80 rounded-full text-muted-foreground hover:text-foreground transition-colors touch-manipulation"
        aria-label="Go back"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground leading-tight">{t("verifyPhone")}</h2>
      <p className="text-sm text-muted-foreground">
        {t.rich("codeSentToPhone", { phone: phoneNumber, strong: (chunks) => <span className="font-medium text-foreground">{chunks}</span> })}
      </p>

      {errorMessage && (
        <div className="p-3.5 rounded-xl bg-destructive/10 text-destructive text-sm border border-destructive/20">
          {errorMessage}
        </div>
      )}
      {infoMessage && (
        <div className="p-3.5 rounded-xl bg-primary/10 text-foreground text-sm border border-primary/20">
          {infoMessage}
        </div>
      )}

      <div className="flex justify-center my-6">
        <StandaloneOTPInput
          length={6}
          onComplete={(code: string) => handleAutoVerify(code)}
          className="py-4"
        />
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={handleResend}
          disabled={loading || isPending}
          className="text-sm text-primary hover:underline disabled:opacity-50"
        >
          Didn’t receive a code? Resend
        </button>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        {tCommon.rich("agreeTerms", { privacy: (chunks) => <Link href="/privacy" className="underline hover:text-foreground">{chunks}</Link>, terms: (chunks) => <Link href="/terms" className="underline hover:text-foreground">{chunks}</Link> })}
      </p>
    </div>
  );
}